import { ExpressServerRoot } from '../constants'

/*
This Redux reducer provides action creators that make API requests, actions that
save fetched data to the local redux store, and convenience getters that allow
the data to be retrieved for display in the UI.

When you make an API request or request cached data you provide a "path", like
['properties', '1'] that corresponds to a REST route `/properties/1`. Using
paths allows the reducer to be generic - we structure the cache as a tree and 
update it as requests are made.
*/

// Getters
function isNumber(n) {
  return `${n / 1}` === `${n}`
}

export function dataForResourcePath(state, path) {
  const { parent, parentKey } = resolveResourcePath(state, path)
  if (parent instanceof Array) {
    return parent.find((i) => i.id / 1 === parentKey / 1)
  } else {
    return parent[parentKey]
  }
}

// Helpers

function resolveResourcePath(state, path) {
  let parent = state
  let child = state

  for (const part of path.slice(0, -1)) {
    if (parent instanceof Array) {
      const id = part / 1
      child = parent.find((i) => i.id / 1 === id)
      if (!child) {
        child = { id }
        parent.push(child)
      }
    } else {
      child = parent[part]
      if (!child) {
        const nextPart = path[path.indexOf(part) + 1]
        child = parent[part] = isNumber(nextPart) ? [] : {}
      }
    }

    parent = child
  }

  return { parent, parentKey: path[path.length - 1] }
}

function upsertDataAtPath(state, path, data) {
  const nextState = JSON.parse(JSON.stringify(state))

  // First, find the leaf node where we're going to insert this data
  // into our local state.
  const { parent, parentKey } = resolveResourcePath(nextState, path)

  if (parent instanceof Array) {
    // If we're saving an item that is in an array, find/replace the existing
    // item with the same ID, or insert it at the end.
    const idx = parent.findIndex((i) => i.id === parentKey / 1)
    if (idx === -1) {
      parent.push(data)
    } else {
      parent[idx] = Object.assign({}, parent[idx], data)
    }
  } else {
    // If we're saving an item that is in an object, upsert the existing data
    // with the new data (so keys not present in the new data are not overwritten)
    if (data instanceof Array) {
      parent[parentKey] = data
    } else {
      parent[parentKey] = Object.assign(parent[parentKey] || {}, data)
    }
  }

  return nextState
}

// Action Types

export const REQUEST_STARTED = 'REQUEST_STARTED'
export const REQUEST_FINISHED = 'REQUEST_FINISHED'

// Action Creators

export function apiRequest(resourcePath, method = 'GET', itemPayload = null) {
  return async (dispatch) => {
    dispatch({
      type: REQUEST_STARTED,
      resourcePath: resourcePath,
    })

    const path = `/api/dashboard/${resourcePath.join('/')}`
    const options = {
      credentials: 'include',
      method,
    }

    if (itemPayload) {
      options.body = JSON.stringify(itemPayload)
      options.headers = {
        'content-type': 'application/json',
      }
    }
    console.log('at API request',path, options)
    const response = await fetch(path, options)

    if (response.status === 401) {
      window.location = `${ExpressServerRoot}/login?next=${encodeURIComponent(
        window.location.href
      )}`
      throw new Error('Redirecting to login')
    }

    const text = await response.text()
    let json = { error: `${response.status} ${response.statusText}` }

    try {
      json = JSON.parse(text)
    } catch (err) {
      console.error(`${method} ${path} returned invalid JSON: ${text}`)
    }

    dispatch({
      type: REQUEST_FINISHED,
      method: method,
      resourcePath: resourcePath,
      resourceJSON: json,
    })

    return { json, response }
  }
}

// Initial State

const initialState = {
  pending: 0,
}

// Reducer

export default function reducer(state = initialState, action) {
  const nextState = Object.assign({}, state)

  switch (action.type) {
    case REQUEST_STARTED:
      nextState.pending += 1
      return nextState

    case REQUEST_FINISHED:
      let { method, resourceJSON, resourcePath } = action

      nextState.pending -= 1

      if (!resourceJSON) {
        return nextState
      }

      if (method === 'POST') {
        resourcePath = [...resourcePath, `${resourceJSON.id}`]
      }

      return upsertDataAtPath(nextState, resourcePath, resourceJSON)

    default:
      return state
  }
}
